import { useState, useEffect, useContext, Suspense, useCallback } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import { isBrowserSize } from '../utilities/utils';
import { ControllerContext } from '../ts/Contexts';
import LazyLoader from '../controls/lazyLoader';
import { useCurrentBreakpointName } from 'react-socks';
import { Check, ExpandLess, ExpandMore, Visibility, Close } from '@mui/icons-material';
import { green } from '@mui/material/colors';
import { pillory_page, votes_page } from '../controls/ProtectedPage';
import { ControlRecord, PilloryTopic, VoteTask } from '../ts/Definitions';
import { lazyWithRetry } from '../utilities/lazyLoaderUtils';
import UnownedMemberTaskAllocator from '../itemViewers/UnownedMemberTaskAllocator';

const PilloryViewer = lazyWithRetry(() => (import('./PilloryViewer')));
const VoteViewer = lazyWithRetry(() => (import('./VoteViewer')));
const VoteItem = lazyWithRetry(() => (import('../itemViewers/VoteItem')));
const PilloryItem = lazyWithRetry(() => (import('../itemViewers/PilloryItem')));


interface Props { command: string | null, path: string | null }

enum GuideTabs { Votes, Pillory, Chastity, UnownedMembers }
const GuidedHome = ({ path, command }: Props): JSX.Element | null => {
    const controller = useContext(ControllerContext);
    const editorSettings = controller.getLoggedOnUserSettings();
    const breakpoint = useCurrentBreakpointName();
    const [category, setCategory] = useState(GuideTabs.UnownedMembers);
    const [requestedVote, setRequestedVote] = useState<VoteTask>();
    const [profile, setProfile] = useState<ControlRecord>();
    const [requestedPillory, setRequestedPillory] = useState<PilloryTopic>();

    const refreshData = useCallback(() => {
        if (command && path) {
            switch (path) {
                case votes_page: {
                    const vote = editorSettings.getVotes().find(v => v.key === command);
                    setRequestedVote(editorSettings.getVotes().find(v => v.key === command));
                    if (vote) setProfile(editorSettings.getProfile(vote.userid));
                    break;
                }
                case pillory_page: {
                    const pill = editorSettings.getPillories().find(v => v.id === command);
                    setRequestedPillory(pill);
                    if (pill) setProfile(editorSettings.getProfile(pill.userid));
                    break;
                }
            }
        }
    }, [command, editorSettings, path]);


    useEffect(() => {
        setCategory(GuideTabs.UnownedMembers);
        editorSettings.subscribeToPillory(refreshData);
        editorSettings.subscribeToVotingTasks(refreshData);
        refreshData();
        return () => {
            editorSettings.unsubscribeToPillory(refreshData);
            editorSettings.unsubscribeToVotingTasks(refreshData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setFilterOverride = (category: GuideTabs) => {
        setCategory(category);
    }

    const welcomeTile = (
        <div className="welcomeTile">
            <p className='memberElementTitle' onClick={() => editorSettings.showSummary('minimiseWelcome')}><Check style={{ color: green[500], paddingRight: '10px', width: '36px', height: '36px' }} />Welcome to Tarlings
                <span className='expandCollapseSection'>
                    {editorSettings.canShowSummary('minimiseWelcome') && <ExpandLess className='expandCollapseSectionButton' onClick={(args) => {
                        editorSettings.hideSummary('minimiseWelcome');
                        args.stopPropagation();
                    }} />}
                    {!editorSettings.canShowSummary('minimiseWelcome') && <ExpandMore className='expandCollapseSectionButton orange' onClick={() => editorSettings.showSummary('minimiseWelcome')} />}
                    {!editorSettings.canShowSummary('welcomeTile') && <span onClick={() => editorSettings.showSummary('welcomeTile')} className='showThisSummary'>{'[Show Info '}<Visibility className='hideSummaryButton' />{']'}</span>}
                </span>
            </p>
            {editorSettings.canShowSummary('welcomeTile') &&
                <DialogContentText className='dialogContentTextHeader memberElementDescription'>{`Thanks for visiting. Tarlings is a way for dominant and submissive people to express themselves and for submissives to perform tasks and activities for dominants.
                If you sign up you can create a profile, you can create your own tasks for people to follow or if you are a submissive you can find an owner to follow. They might even take ownership of your session and control you for real.
                For now, find the list of pillories and tasks that are currently open to be voted and feel free to add to their times!
                
                You can also find a list of Submissives who are not owned by anyone, that you can feel free to add tasks to their sessions. Make a sub's day!`}
                    <span onClick={() => editorSettings.hideSummary('welcomeTile')} className='hideThisText'>{'[Hide'}<Close className='hideSummaryButton' />{']'}</span>
                </DialogContentText>}
        </div>
    );

    const isBrowserMode = isBrowserSize(breakpoint);
    return (
        <Suspense fallback={<LazyLoader
            status="Loading your welcome..."
            delay={100} />}>
            <div className='multiPaneContainer noSideBar'>
                <div className='layoutTile'>
                    {welcomeTile}
                </div>
                <div className={isBrowserMode ? 'navigationComponentGuest sticky' : 'mobileNavigationComponentGuest sticky'}>
                    <Button size='small'
                        className={`${category === GuideTabs.UnownedMembers ? 'selectedCategory' : 'categoryButton'}`}
                        onClick={() => setFilterOverride(GuideTabs.UnownedMembers)}>Set Tasks</Button>
                    <Button size='small'
                        className={`${category === GuideTabs.Pillory ? 'selectedCategory' : 'categoryButton'}`}
                        onClick={() => setFilterOverride(GuideTabs.Pillory)}>Pillory</Button>
                    <Button size='small'
                        className={`${category === GuideTabs.Chastity ? 'selectedCategory' : 'categoryButton'}`}
                        onClick={() => setFilterOverride(GuideTabs.Chastity)}>Chastity</Button>
                    <Button size='small'
                        className={`${category === GuideTabs.Votes ? 'selectedCategory' : 'categoryButton'}`}
                        onClick={() => setFilterOverride(GuideTabs.Votes)}>Votes</Button>
                </div>
                {category === GuideTabs.Pillory &&
                    <PilloryViewer guestMode />
                }
                {(category === GuideTabs.Votes || category === GuideTabs.Chastity) &&
                    <VoteViewer splitMode isChastity={category === GuideTabs.Chastity} guestMode />
                }
                {category === GuideTabs.UnownedMembers
                    && <UnownedMemberTaskAllocator />
                }
            </div>
            {profile && path && command && (requestedPillory || requestedVote)
                && (requestedPillory?.canVote(editorSettings.getControl().userid, controller.cookiesService)
                    || requestedVote?.canVote(editorSettings.getControl().userid, controller.cookiesService)) && <Dialog maxWidth={'xl'} open>
                    <DialogContent style={{ padding: 10 }}>
                        {path === votes_page && requestedVote && <VoteItem guestMode vote={requestedVote} profile={profile} />}
                        {path === pillory_page && requestedPillory && <PilloryItem guestMode pillory={requestedPillory} profile={profile} />}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setRequestedPillory(undefined);
                            setRequestedVote(undefined);
                        }}>Close</Button>
                    </DialogActions>
                </Dialog>}
        </Suspense>
    );
};

export default GuidedHome;